import { getLocalTableStoreCloudTenantId } from '@infra/persistence/LocalTableStore';
import { TenantContext } from '@infra/tenancy/TenantContext';

const ONBOARDING_KEY_PREFIX = 'nexxus.onboarding.v1';
/** Ancienne clé globale (mélange multi-entreprises) — migration douce. */
const GLOBAL_LEGACY_KEY = 'nexxus.onboarding.v1';

export interface OnboardingProgress {
  dismissed: boolean;
  entreprise: boolean;
  /** Étape 2 wizard — taux légaux consultés (Paramètres). */
  taux: boolean;
  salarie: boolean;
  bulletin: boolean;
  /** Étape 5 wizard — PDF bulletin téléchargé. */
  pdf: boolean;
}

function emptyProgress(): OnboardingProgress {
  return {
    dismissed: false,
    entreprise: false,
    taux: false,
    salarie: false,
    bulletin: false,
    pdf: false,
  };
}

/** BUG4 — checklist cloisonnée par dossier (pas de progression du client A chez B). */
function resolveTenantId(): string {
  return (
    TenantContext.getInstance().tryGetTenant()?.id ??
    (getLocalTableStoreCloudTenantId() !== 'default'
      ? getLocalTableStoreCloudTenantId()
      : 'default')
  );
}

function storageKey(tenantId: string): string {
  return `${ONBOARDING_KEY_PREFIX}.${tenantId}`;
}

function load(): OnboardingProgress {
  const tenantId = resolveTenantId();
  try {
    const scoped = localStorage.getItem(storageKey(tenantId));
    if (scoped) {
      return { ...emptyProgress(), ...JSON.parse(scoped) };
    }
    // Migration one-shot depuis la clé globale (dossier courant uniquement).
    if (tenantId !== 'default') {
      const legacy = localStorage.getItem(GLOBAL_LEGACY_KEY);
      if (legacy) {
        const parsed = { ...emptyProgress(), ...JSON.parse(legacy) };
        localStorage.setItem(storageKey(tenantId), JSON.stringify(parsed));
        localStorage.removeItem(GLOBAL_LEGACY_KEY);
        return parsed;
      }
    }
    return emptyProgress();
  } catch {
    return emptyProgress();
  }
}

function save(progress: OnboardingProgress): void {
  const tenantId = resolveTenantId();
  localStorage.setItem(storageKey(tenantId), JSON.stringify(progress));
  try {
    localStorage.removeItem(GLOBAL_LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

export function markOnboardingStep(step: keyof Omit<OnboardingProgress, 'dismissed'>): void {
  const progress = load();
  if (progress[step]) return;
  progress[step] = true;
  save(progress);
}

export function dismissOnboarding(): void {
  const progress = load();
  progress.dismissed = true;
  save(progress);
}

export function getOnboardingProgress(): OnboardingProgress {
  return load();
}

/**
 * Checklist 1ʳᵉ connexion — délègue au wizard 5 étapes (ONBOARD-01).
 * Conservé pour les appels existants (`renderOnboardingChecklist`).
 */
export function renderOnboardingChecklist(host: HTMLElement): void {
  void import('./OnboardingWizard').then(({ renderOnboardingWizard }) => {
    renderOnboardingWizard(host);
  });
}
